import { Floor } from "@/types/globalTypes";
import TablesFilter from "./TablesFilter";
import TablesPageHeader from "./TablesPageHeader";
import TablesList from "./TablesList";
import { useEffect, useState } from "react";
import ErrorMessage from "../ErrorMessage";
import { useSelector } from "react-redux";
import axios from "axios";
import { useTranslation } from "react-i18next";

type TablesLogicWrapperProps = {
  branchId: string;
};

function TablesLogicWrapper({ branchId }: TablesLogicWrapperProps) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [AllFloors, setAllFloors] = useState<Floor[]>([]);
  const [selectedFloor, setSelectedFloor] = useState<Floor>({} as Floor);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [needRefetch, setNeedRefetch] = useState(false);

  const baseUrl = import.meta.env.VITE_BASE_URL;
  const token = useSelector(
    (state: { auth: { token: string } }) => state.auth.token,
  );

  useEffect(() => {
    if (!token) return;

    setIsLoading(true);
    setError(null);
    setNeedRefetch(false);

    async function getFloors() {
      const url = `${baseUrl}/api/dashboard/floors?branch=${branchId}&locale=${lang}`;

      try {
        const currentFloorId = selectedFloor?._id;

        const res = await axios.get(url, {
          headers: {
            Authorization: token,
          },
        });

        if (!res.data.success) {
          const message = res.data.error || t("floors.fetchFailed");
          setError(message);
          return;
        }

        const newFloors = res.data.floors || [];
        setAllFloors(newFloors);

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const restoredFloor = newFloors.find((floor: any) => floor._id === currentFloorId);

        setSelectedFloor(restoredFloor || newFloors[0] || {});
      } catch (error: unknown) {
        if (axios.isAxiosError(error)) {
          const message =
            error.response?.data?.error || t("floors.fetchFailed");
          setError(message);
        } else {
          console.error(error);
        }
      } finally {
        setIsLoading(false);
      }
    }


    getFloors();
  }, [baseUrl, branchId, lang, token, t, needRefetch, selectedFloor?._id]);

  if (error) return <ErrorMessage msg={error} />;

  return (
    <>
      <TablesPageHeader setNeedRefetch={setNeedRefetch} branchId={branchId} />
      <TablesFilter
        floors={AllFloors}
        setSelectedFloor={setSelectedFloor}
        selectedFloor={selectedFloor}
        isLoading={isLoading}
      />
      <TablesList
        floorId={selectedFloor?._id}
        floorNumber={selectedFloor?.number}
        tables={selectedFloor?.tables}
        isLoading={isLoading}
        setNeedRefetch={setNeedRefetch}
      />
    </>
  );
}

export default TablesLogicWrapper;
